import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Icon, Badge, HStack, UnorderedList, ListItem, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon } from "@chakra-ui/react";
import { FaWrench, FaCalendarCheck, FaExclamationTriangle, FaShieldAlt, FaSun, FaWater } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const NAVY = "#141C49";

const SolarResourcesMaintenance: React.FC = () => {
  const maintenanceTasks = [
    {
      frequency: "Monthly",
      icon: FaSun,
      tasks: [
        "Visual inspection for damage or debris",
        "Check monitoring app for performance drops",
        "Look for shading from new tree growth",
        "Inspect for bird nesting or droppings"
      ]
    },
    {
      frequency: "Quarterly",
      icon: FaWater,
      tasks: [
        "Clean panels with water and soft brush",
        "Remove leaves, dirt, and accumulated debris",
        "Check panel mounting for loose fixings",
        "Inspect wiring and connections for wear"
      ]
    },
    {
      frequency: "Annually",
      icon: FaCalendarCheck,
      tasks: [
        "Professional system health check",
        "Inverter performance assessment",
        "Electrical connection testing",
        "Update monitoring system firmware",
        "Review energy production vs. expectations"
      ]
    },
    {
      frequency: "5 Years",
      icon: FaShieldAlt,
      tasks: [
        "Comprehensive electrical safety inspection",
        "Replace worn DC isolators if needed",
        "Check structural integrity of mounting",
        "Consider inverter replacement (10-12 year lifespan)"
      ]
    }
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/resources/maintenance"
        fallbackTitle="Solar Maintenance Guide"
        fallbackDescription="Best practices for maintaining and caring for your solar system"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaWrench} boxSize={12} />
            <Heading size="2xl" fontWeight="bold">
              Solar System Maintenance Guide
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="3xl">
              Keep your solar panels performing at peak efficiency for 25+ years
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Maintenance Schedule */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Badge bg={SOLAR_GOLD} color="white" fontSize="md" px={4} py={2}>MAINTENANCE SCHEDULE</Badge>
          <Heading size="xl" textAlign="center">Regular Maintenance Tasks</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="3xl">
            Follow this schedule to ensure optimal system performance and longevity
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {maintenanceTasks.map((task, index) => (
            <Box
              key={index}
              p={8}
              bg="white"
              borderRadius="xl"
              border="3px solid"
              borderColor={SOLAR_GOLD}
              shadow="md"
            >
              <HStack spacing={4} mb={4}>
                <Icon as={task.icon} boxSize={10} color={SOLAR_GOLD} />
                <VStack align="flex-start" spacing={0}>
                  <Text fontSize="sm" color="gray.600" fontWeight="semibold">EVERY</Text>
                  <Heading size="lg" color={NAVY}>{task.frequency}</Heading>
                </VStack>
              </HStack>

              <UnorderedList spacing={2} pl={4}>
                {task.tasks.map((item, idx) => (
                  <ListItem key={idx} color="gray.700">
                    {item}
                  </ListItem>
                ))}
              </UnorderedList>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Common Issues Accordion */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8} mb={12}>
            <Badge bg={NAVY} color="white" fontSize="md" px={4} py={2}>TROUBLESHOOTING</Badge>
            <Heading size="xl" textAlign="center">Common Issues & Solutions</Heading>
          </VStack>

          <Accordion allowMultiple>
            <AccordionItem border="2px solid" borderColor="gray.200" borderRadius="lg" mb={4}>
              <AccordionButton bg="white" _hover={{ bg: "gray.50" }} p={6}>
                <HStack flex="1" spacing={4}>
                  <Icon as={FaExclamationTriangle} color={SOLAR_GOLD} boxSize={6} />
                  <Heading size="md">Reduced Energy Production</Heading>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={6} pt={4} bg="white">
                <VStack align="stretch" spacing={3}>
                  <Text fontWeight="bold">Possible Causes:</Text>
                  <UnorderedList pl={6}>
                    <ListItem>Dirty or dusty panels reducing efficiency</ListItem>
                    <ListItem>New shading from trees, buildings, or structures</ListItem>
                    <ListItem>Inverter malfunction or error</ListItem>
                    <ListItem>Seasonal variation (winter vs. summer)</ListItem>
                  </UnorderedList>
                  <Text fontWeight="bold" mt={2}>Solutions:</Text>
                  <UnorderedList pl={6}>
                    <ListItem>Clean panels with water and soft brush</ListItem>
                    <ListItem>Trim overhanging branches</ListItem>
                    <ListItem>Check inverter display for error codes</ListItem>
                    <ListItem>Compare current production to last year same period</ListItem>
                  </UnorderedList>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem border="2px solid" borderColor="gray.200" borderRadius="lg" mb={4}>
              <AccordionButton bg="white" _hover={{ bg: "gray.50" }} p={6}>
                <HStack flex="1" spacing={4}>
                  <Icon as={FaExclamationTriangle} color={SOLAR_GOLD} boxSize={6} />
                  <Heading size="md">System Not Producing Any Power</Heading>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={6} pt={4} bg="white">
                <VStack align="stretch" spacing={3}>
                  <Text fontWeight="bold">Possible Causes:</Text>
                  <UnorderedList pl={6}>
                    <ListItem>Tripped circuit breaker or blown fuse</ListItem>
                    <ListItem>Grid outage (safety feature shuts down system)</ListItem>
                    <ListItem>Inverter failure or shutdown</ListItem>
                    <ListItem>DC isolator switched off accidentally</ListItem>
                  </UnorderedList>
                  <Text fontWeight="bold" mt={2}>Solutions:</Text>
                  <UnorderedList pl={6}>
                    <ListItem>Check main switchboard for tripped breakers</ListItem>
                    <ListItem>Verify grid power is available</ListItem>
                    <ListItem>Check DC isolator is in ON position</ListItem>
                    <ListItem>Contact your installer if issue persists</ListItem>
                  </UnorderedList>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem border="2px solid" borderColor="gray.200" borderRadius="lg" mb={4}>
              <AccordionButton bg="white" _hover={{ bg: "gray.50" }} p={6}>
                <HStack flex="1" spacing={4}>
                  <Icon as={FaExclamationTriangle} color={SOLAR_GOLD} boxSize={6} />
                  <Heading size="md">Inverter Display Showing Error</Heading>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={6} pt={4} bg="white">
                <VStack align="stretch" spacing={3}>
                  <Text>Modern inverters have built-in diagnostics that display error codes when issues occur.</Text>
                  <Text fontWeight="bold" mt={2}>Steps to Take:</Text>
                  <UnorderedList pl={6}>
                    <ListItem>Note the exact error code or message</ListItem>
                    <ListItem>Take a photo of the inverter display</ListItem>
                    <ListItem>Check inverter manual for error code meaning</ListItem>
                    <ListItem>Try power cycling (off for 5 minutes, then on)</ListItem>
                    <ListItem>Contact installer with error code if unresolved</ListItem>
                  </UnorderedList>
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Container>
      </Box>

      {/* Safety Tips */}
      <Container maxW="container.xl" py={16}>
        <Box p={8} bg="gray.50" borderRadius="xl" border="2px solid" borderColor={NAVY}>
          <VStack spacing={4} align="stretch">
            <HStack spacing={4}>
              <Icon as={FaShieldAlt} boxSize={10} color={NAVY} />
              <Heading size="lg" color={NAVY}>Safety First</Heading>
            </HStack>
            <Text color="gray.700">
              Solar panels generate electricity whenever exposed to light, even on cloudy days. Never attempt to:
            </Text>
            <UnorderedList pl={8} spacing={2}>
              <ListItem color="gray.700">Work on electrical components yourself</ListItem>
              <ListItem color="gray.700">Touch exposed wiring or connections</ListItem>
              <ListItem color="gray.700">Clean panels while standing on the roof (hire professionals)</ListItem>
              <ListItem color="gray.700">Use high-pressure washers (can damage panels)</ListItem>
              <ListItem color="gray.700">Use abrasive cleaners or hard brushes</ListItem>
            </UnorderedList>
            <Text color="gray.700" fontWeight="bold">
              Always contact a licensed solar technician for electrical work or roof access.
            </Text>
          </VStack>
        </Box>
      </Container>

      {/* Warranty Information */}
      <Box bg={brandConfig.colors.primary} color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl">Warranty Coverage</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full" pt={4}>
              <Box>
                <Text fontSize="3xl" fontWeight="bold">25 Years</Text>
                <Text fontSize="lg">Panel Performance</Text>
                <Text fontSize="sm" opacity={0.8}>80%+ output guaranteed</Text>
              </Box>
              <Box>
                <Text fontSize="3xl" fontWeight="bold">10-12 Years</Text>
                <Text fontSize="lg">Inverter Warranty</Text>
                <Text fontSize="sm" opacity={0.8}>Extended options available</Text>
              </Box>
              <Box>
                <Text fontSize="3xl" fontWeight="bold">10 Years</Text>
                <Text fontSize="lg">Workmanship</Text>
                <Text fontSize="sm" opacity={0.8}>Installation guarantee</Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarResourcesMaintenance;
